import type { CurrencyCode } from '@/lib/checkout/types'
import { formatCurrency } from '@/lib/checkout/utils'

import { IntroOfferType, type Price } from '@/clients/payment-service'

export type PriceDisplayAs = 'base' | 'perDay' | 'perMonth'

export const DEFAULT_DISPLAY_AS: PriceDisplayAs = 'base'

export const TRIAL_INTERVAL_METADATA_KEY = 'trial_interval'
export const TRIAL_INTERVAL_COUNT_METADATA_KEY = 'trial_interval_count'
export const TRIAL_RENEWAL_LOOKUP_KEY_METADATA_KEY = 'trial_price_for_lookup_key'
const EXPERIMENT_FLAG_METADATA_KEY = 'experiment_flag'

const DAYS_PER_MONTH = 30

const DAYS_PER_INTERVAL: Record<string, number> = {
  DAY: 1,
  WEEK: 7,
  MONTH: DAYS_PER_MONTH,
  YEAR: 365,
}

const MONTHS_PER_INTERVAL: Record<string, number> = {
  DAY: 1 / DAYS_PER_MONTH,
  WEEK: 7 / DAYS_PER_MONTH,
  MONTH: 1,
  YEAR: 12,
}

const getTrialInterval = (price: Price): { interval: string; count: number } | undefined => {
  const interval = price.metadata?.[TRIAL_INTERVAL_METADATA_KEY]
  const count = Number(price.metadata?.[TRIAL_INTERVAL_COUNT_METADATA_KEY])
  if (!interval || !Number.isFinite(count) || count < 1) return undefined

  return { interval, count }
}

/** The key of the recurring price a trial converts into, or undefined for a price that is not a trial. */
export const getRenewalLookupKey = (price: Price): string | undefined =>
  price.metadata?.[TRIAL_RENEWAL_LOOKUP_KEY_METADATA_KEY]

const getDuration = (price: Price, unitsPerInterval: Record<string, number>): number | undefined => {
  const period = price.recurring
    ? { interval: price.recurring.interval, count: price.recurring.interval_count }
    : getTrialInterval(price)

  if (!period) return undefined

  const units = unitsPerInterval[period.interval.toUpperCase()]
  if (!units || period.count < 1) return undefined

  return units * period.count
}

export const getDurationDays = (price: Price): number | undefined => getDuration(price, DAYS_PER_INTERVAL)

export const getDurationMonths = (price: Price): number | undefined => getDuration(price, MONTHS_PER_INTERVAL)

/**
 * True for an intro fee: a one-off fee that starts a subscription on the price it names, matching how the
 * payment-service identifies one. Both a trial and the welcome offer are intro fees.
 */
export const isIntroFee = (price?: Price): boolean => !!price && !price.recurring && !!getRenewalLookupKey(price)

/**
 * True for an intro fee sold as a ZOE trial, false for the welcome offer and any other price. A fee without
 * `intro_offer_type` comes from a payment-service that doesn't send it yet, and every such fee is a trial.
 */
export const isZoeTrial = (price?: Price): boolean =>
  isIntroFee(price) && price?.intro_offer_type !== IntroOfferType.WELCOME_OFFER

/** True for a price sold only in an experiment arm, tagged with the experiment's flag in its metadata. */
export const isExperimentPrice = (price: Price): boolean => !!price.metadata?.[EXPERIMENT_FLAG_METADATA_KEY]

/** Trial length in days, or undefined for a price that is not a trial. */
export const getTrialLength = (price?: Price): number | undefined =>
  price && isIntroFee(price) ? getDurationDays(price) : undefined

export const getDisplayAmount = (price: Price, displayAs: PriceDisplayAs): number | undefined => {
  if (displayAs === 'base') return price.unit_amount

  const duration = displayAs === 'perDay' ? getDurationDays(price) : getDurationMonths(price)
  if (!duration) return undefined

  return price.unit_amount / duration
}

export const formatDisplayPrice = (price: Price, displayAs: PriceDisplayAs, locale: string): string | undefined => {
  const amount = getDisplayAmount(price, displayAs)
  if (amount === undefined) return undefined

  return formatCurrency(price.currency as CurrencyCode, amount, true, locale)
}

export type PriceResolver = (lookupKey?: string | null, displayPriceAs?: PriceDisplayAs | null) => string | undefined

const TOKEN_PATTERN = /\{(\w+)(?::(\w+))?\}/g

const DISPLAY_TOKENS: PriceDisplayAs[] = ['base', 'perDay', 'perMonth']

const isValidDisplayToken = (value?: string): value is PriceDisplayAs =>
  !!value && DISPLAY_TOKENS.includes(value as PriceDisplayAs)

export const resolvePricesInCopy = (
  copy: string | null | undefined,
  lookupKeys: Record<string, string | null | undefined>,
  resolvePrice: PriceResolver,
): string => {
  if (!copy) return ''

  return copy.replace(TOKEN_PATTERN, (token, name: string, mode?: string) => {
    if (!(name in lookupKeys)) return token

    if (mode && !isValidDisplayToken(mode)) return token

    return resolvePrice(lookupKeys[name], isValidDisplayToken(mode) ? mode : undefined) ?? token
  })
}

const LOOKUP_KEY_FIELD = /lookupkey/i

export type UnresolvedLookupKey = {
  field: string
  lookupKey: string
}

const collectLookupKeys = (node: unknown, found: UnresolvedLookupKey[], lookupKeyField?: string): void => {
  if (!node) return

  if (typeof node === 'string') {
    if (lookupKeyField) found.push({ field: lookupKeyField, lookupKey: node })
    return
  }

  if (typeof node !== 'object') return

  if (Array.isArray(node)) {
    node.forEach((item) => collectLookupKeys(item, found, lookupKeyField))
    return
  }

  Object.entries(node).forEach(([field, value]) => {
    collectLookupKeys(value, found, lookupKeyField ?? (LOOKUP_KEY_FIELD.test(field) ? field : undefined))
  })
}

const findUnresolvedLookupKeys = (content: unknown, prices: Price[]): UnresolvedLookupKey[] => {
  const resolved = new Set(prices.map((price) => price.lookup_key))
  const found: UnresolvedLookupKey[] = []

  collectLookupKeys(content, found)

  const unresolved = found.filter(({ lookupKey }) => !resolved.has(lookupKey))

  return [...new Map(unresolved.map((entry) => [`${entry.field}|${entry.lookupKey}`, entry])).values()]
}

/** Throws if any `*lookupKey*` field in the DatoCMS content has no matching payment-service price. */
export const validateLookupKeyFields = (content: unknown, prices: Price[], source: string): void => {
  if (prices.length === 0) return

  const unresolved = findUnresolvedLookupKeys(content, prices)
  if (unresolved.length === 0) return

  const detail = unresolved.map(({ field, lookupKey }) => `${field}="${lookupKey}"`).join(', ')

  throw new Error(`${source}: DatoCMS lookup keys with no payment-service price: ${detail}`)
}
